<template>
  <NavigationLayout
    :navigation-title="title"
    :navigation-bar-style="navigationBarStyle"
    :navigation-content-style="navigationContentStyle"
    v-if="!isLoading"
  >
    <div v-if="!showNotFound()" slot="contents">
      <div class="continuous_count_container">
        <div class="header">
          <div class="header_title">
            {{ $t('chargeGradeContinuationStatusTitle') }}
          </div>
        </div>
        <div class="list_container">
          <div
            v-for="grade in continuousCountList"
            :key="grade.gradeId"
            class="achievement_list_item"
            :class="{ 'is_continuous': grade.lastMasterId === chargeGradeMasterId }"
          >
            <div class="item_content">
              <div class="grade_badge">
                <img :src="grade.iconUrl" />
              </div>
              <div class="item_text">
                <div class="grade_info">
                  <span class="grade_name">{{ grade.gradeName }}</span>
                  <span class="achievement_date" v-if="grade.count > 0">
                    {{ $t('chargeGradeContinuationStatusMonths', { count: grade.count }) }}
                  </span>
                </div>
              </div>
            </div>
            <div class="continuous_status_overlay" v-if="grade.lastMasterId !== chargeGradeMasterId || grade.count === 0"></div>
          </div>
        </div>
      </div>
    </div>
    <not-found
      v-show="showNotFound()"
      class="continuous_count_notfound"
      slot="contents"
    />
  </NavigationLayout>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator'
import { LanguageQueryHandler } from '@/mixins/LanguageQuery'
import NavigationLayout from '@/components/NavigationLayout.vue'
import { api } from '@/api/appGatewayServerApi'
import { checkInMaintenance } from '@/utils/maintenance'
import { handleUnauthorized } from '@/utils/handle_error'
import { ApiError } from '@/api/error'
import NotFound from '@/components/Error/NotFound.vue'
import * as models from '@/models'
import { getTokenFromCookie } from '@/utils/token'

@Component({
  components: {
    NotFound,
    NavigationLayout,
  },
})
export default class ChargeGradeContinuationStatus extends Mixins(
  LanguageQueryHandler
) {
  // 課金グレード継続回数リスト
  continuousCountList: models.ChargeGradeContinuousCount[] = []
  // 現在のマスターID
  chargeGradeMasterId: number | undefined = undefined
  // データが見つからない状態を示すフラグ
  isNotFound: boolean = false
  // データロード中を示すフラグ
  isLoading: boolean = true

  created() {
    this.fetchData()
  }

  async fetchData() {
    this.isLoading = true
    try {
      // グレード連続達成状況の取得
      const response = await api().charge_grade_web.continuous_count.get()
      
      this.continuousCountList = response.continuousCountList
      this.chargeGradeMasterId = response.chargeGradeMasterId
    } catch (error) {
      if (checkInMaintenance(error)) {
        return
      }

      if (!handleUnauthorized(error)) {
        // 401エラー以外はエラーとして扱う
        this.isNotFound = true
      }
    } finally {
      this.isLoading = false
    }
  }

  /**
   * 「見つかりません」画面を表示するかどうかを判定
   * ローディング中は表示せず、APIからデータが見つからない場合のみ表示する
   */
  showNotFound() {
    return this.isNotFound && !this.isLoading
  }

  isWebView() {
    return getTokenFromCookie() !== undefined
  }

  get title() {
    // nullをわたすとタイトルロゴが表示され、stringを渡すとその文字列が表示される
    return this.showNotFound() || this.isWebView() ? '' : null
  }
  
  get navigationContentStyle() {
    if (this.isWebView()) {
      return {
        'padding-top': 'var(--safe-area-inset-top, 0px)',
      }
    }
    return {
      'padding-top': '44px',
    }
  }
  
  get navigationBarStyle() {
    if (this.isWebView()) {
      return {
        'flex-direction': 'row',
        'justify-content': 'center',
        'z-index': '2',
        'top': 'var(--safe-area-inset-top, 0px)',
        height: '0px',
      }
    }
    return {
      'flex-direction': 'row',
      'justify-content': 'center',
      'z-index': '2',
      'border-bottom': '1px solid #e8e8e8',
      height: '44px',
    }
  }
}
</script>

<style scoped>
.continuous_count_container {
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  min-height: 100vh;
  background: #FFFFFF;
  padding: 0 0 var(--safe-area-inset-bottom, 0px) 0;
}

.header {
  width: 100%;
  height: 44px;
  position: relative;
  background: #FFFFFF;
}

.header_title {
  position: absolute;
  min-width: min-content;
  height: 26px;
  left: 50%;
  bottom: 8px;
  transform: translateX(-50%);
  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 17px;
  line-height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #262626;
  white-space: nowrap;
}

/* current_master_info は非表示のため削除 */

.list_container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
}

.achievement_list_item {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px 16px;
  gap: 4px;
  width: 100%;
  height: 56px;
  background: #FFFFFF;
  border: 0.5px solid #E8E8E8;
  border-radius: 8px;
  position: relative;
}

.item_content {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 4px;
  width: 100%;
  height: 32px;
}

.grade_badge {
  width: 28px;
  height: 28px;
  flex: none;
  order: 0;
  flex-grow: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.grade_badge img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.item_text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0;
  flex: 1;
  height: 32px;
  min-width: 0; /* flexboxの子要素がはみ出す問題を解決 */
  margin: 0 0 0 1px; /* 左側だけに最小限の余白 */
}

.grade_info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1px;
  width: 100%;
  justify-content: space-between;
}

.grade_name {
  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #262626;
  white-space: nowrap;
}

.achievement_date {
  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 300;
  font-size: 12px;
  line-height: 130%;
  color: #188DFF;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.count_value {
  font-family: 'Hiragino Sans', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  color: #262626;
  margin-left: auto;
  white-space: nowrap;
  min-width: 30px; /* 数値の最小幅を確保 */
  text-align: right;
}

.continuous_status_overlay {
  position: absolute;
  height: 56px;
  left: 0;
  right: 0;
  top: 0;
  background: rgba(15, 15, 15, 0.4);
  border-radius: 8px;
}

.continuous_count_notfound {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
}

/* スマートフォン向けの調整 */
@media screen and (max-width: 375px) {
  .continuous_count_container,
  .list_container {
    width: 100%;
  }
}
</style>

